import { useState } from "react";
import { Box, Container, Typography, Paper } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { useSnackbar } from "notistack";
import Form from "../Forms/Form";
import { FormField } from "../Forms/FormField";
import { FieldTypes } from "../Forms/FieldTypes";
import * as Yup from "yup";
import axios from "axios";
import { Copyright } from "../Layout/Copyright";
import { useBranding } from "../../branding/BrandingProvider";
import { authErrorMessage } from "../Auth/authErrorMessage";

/**
 * NEW-57(b). The way back in for a patient whose invitation email never arrived.
 *
 * The API answers this with one sentence whether or not an invitation exists for the address, so
 * the screen shows exactly what the server said and nothing more — it must not be the place where
 * "we found you" and "we didn't" become distinguishable. The only failure worth wording here is the
 * bare 429 from the "patient-signup" rate limiter, which has no body of its own.
 */
const ResendPatientInvitation = () => {
  const brand = useBranding();
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const api = useApi(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      const response = await api.post<{ message?: string }>(
        "/invitation/patient/resend",
        { email: values.email },
        { suppressErrorDialog: true },
      );
      setConfirmation(
        response.data?.message ??
          "If an invitation is waiting for that address, we've sent a fresh link.",
      );
    } catch (error) {
      const response = axios.isAxiosError(error) ? error.response : undefined;
      enqueueSnackbar(
        authErrorMessage(
          response?.status ?? 0,
          response?.data,
          "We couldn't resend the email just now. Please try again.",
        ),
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const fields: FormField[] = [
    {
      id: "email",
      email: true,
      text: "Email Address",
      fixedLabel: true,
      type: FieldTypes.Text,
      columns: 12,
      yupSchema: Yup.string()
        .email("Invalid email address")
        .required("Email is required")
        .max(254, "Email address is too long"),
    },
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ pt: 8, pb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <img
            src={brand.logo}
            alt={`${brand.displayName} logo`}
            style={{ height: "80px" }}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            width: "100%",
            maxWidth: 600,
            mx: "auto",
            borderRadius: 2,
            boxShadow: (theme) => theme.palette.brand.shadow.raised,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: "primary.dark",
              fontWeight: 700,
              mb: 4,
              textAlign: "center",
            }}
          >
            Resend your invitation
          </Typography>

          {confirmation === null ? (
            <>
              <Typography
                variant="body1"
                sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}
              >
                Started creating an account but never got the email? Enter the
                address you signed up with and we'll send the link again.
              </Typography>

              <Form
                fields={fields}
                onSubmit={handleSubmit}
                submitButtonText={loading ? "Sending..." : "Resend invitation email"}
                fieldVariant="outlined"
                centerActionButtons
              />
            </>
          ) : (
            <Typography
              variant="body1"
              sx={{ textAlign: "center", color: "text.secondary" }}
            >
              {confirmation}
            </Typography>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <RouterLink
              to="/login"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Back to login
              </Typography>
            </RouterLink>
          </Box>
        </Paper>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Copyright />
      </Box>
    </Container>
  );
};

export default ResendPatientInvitation;
